/**
 * DriveFilePicker — "Add from Google Drive" for the chat composer.
 *
 * Mirrors the Claude.ai / ChatGPT flow: one search box that takes either a
 * query or a pasted Drive link, a Recent list as the first screen, a Browse
 * tab for people who think in folders, multi-select, and a single Add button.
 * The picked files become composer chips; nothing is fetched until the
 * message is sent (the backend downloads them with the user's own token).
 *
 * Listing the person's own Drive needs Google's `read` level (drive.readonly),
 * which a first connection does not grant: until it is granted the dialog
 * asks for it in place (GoogleAccessPrompt) and opens on Recent once allowed.
 * Google's own Picker was weighed instead — it would avoid the restricted
 * scope, but it is Google's dialog, with no Recent list of this shape, no
 * pasted-link path and no RTL of ours; the experience stays this one.
 *
 * Keyboard: ↑/↓ move, Space/Enter toggle, ⌘/Ctrl+Enter adds, Esc closes.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Spin } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { useCloudConnections } from '@/modules/data-bundles/hooks/useCloudConnections';
import { GoogleAccessPrompt } from '@/modules/cloud-storage/components/GoogleAccessPrompt';
import { useGoogleReadGate } from '@/modules/cloud-storage/hooks/useGoogleReadGate';
import { formatRelativeTime } from '@/utils/relativeTime';
import type { DriveFileRef } from '@/types/models';
import { driveService, driveKind, parseDriveFileId, type DriveFolder } from '../../services/driveService';
import { DriveKindIcon, GoogleDriveGlyph } from './driveIcons';
import './DriveFilePicker.css';

interface DriveFilePickerProps {
  open: boolean;
  /** Ids already sitting in the composer — shown as selected, can't be added twice. */
  alreadyAttached: string[];
  maxSelectable: number;
  onAdd: (files: DriveFileRef[]) => void;
  onClose: () => void;
}

type Tab = 'recent' | 'browse';

interface Crumb {
  id: string | null;
  name: string;
}

function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DriveFilePicker({ open, alreadyAttached, maxSelectable, onAdd, onClose }: DriveFilePickerProps) {
  const { t } = useLanguage();
  const { loading: connectionsLoading, loadConnections, connect, getConnection } = useCloudConnections();
  const connection = getConnection('google_drive');
  const gate = useGoogleReadGate(open && !!connection);
  const canList = !!connection && gate.canList;

  const [tab, setTab] = useState<Tab>('recent');
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState<DriveFileRef[] | null>(null);
  const [results, setResults] = useState<DriveFileRef[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [folders, setFolders] = useState<DriveFolder[]>([]);
  const [folderFiles, setFolderFiles] = useState<DriveFileRef[]>([]);
  const [crumbs, setCrumbs] = useState<Crumb[]>([{ id: null, name: '' }]);
  const [browsing, setBrowsing] = useState(false);
  const [selected, setSelected] = useState<Map<string, DriveFileRef>>(new Map());
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchSeq = useRef(0);

  // Reset every time the picker opens.
  useEffect(() => {
    if (!open) return;
    setTab('recent');
    setQuery('');
    setResults(null);
    setError(null);
    setSelected(new Map());
    setCursor(0);
    setCrumbs([{ id: null, name: '' }]);
    gate.reset();
    void loadConnections();
    const focus = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(focus);
  }, [open, loadConnections]);

  // Recent — the first screen. Loaded once per open, once we know Drive is connected.
  useEffect(() => {
    if (!open || !canList) return;
    let cancelled = false;
    setRecent(null);
    driveService.listRecent()
      .then((files) => { if (!cancelled) setRecent(files); })
      .catch((err) => {
        if (cancelled) return;
        setRecent([]);
        // 409 = the connection is gone (revoked / expired) — re-read the
        // connection list so the dialog flips to "Connect Google Drive".
        if (err?.response?.status === 409) void loadConnections();
        else if (!gate.refusedBy(err)) setError(t('chat.drive.loadFailed'));
      });
    return () => { cancelled = true; };
  }, [open, connection?.connection_id, canList]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search / link resolution, debounced. A pasted link resolves to one file.
  useEffect(() => {
    if (!open || !canList) return;
    const q = query.trim();
    if (!q) { setResults(null); setSearching(false); return; }
    const seq = ++searchSeq.current;
    setSearching(true);
    setError(null);
    const handle = window.setTimeout(async () => {
      try {
        const linkId = parseDriveFileId(q);
        const files = linkId
          ? [await driveService.getFile(linkId)]
          : await driveService.search(q);
        if (seq === searchSeq.current) { setResults(files); setCursor(0); }
      } catch (err: any) {
        if (seq !== searchSeq.current) return;
        setResults([]);
        const status = err?.response?.status;
        if (status === 409) { void loadConnections(); return; }
        if (gate.refusedBy(err)) return;
        setError(status === 404 ? t('chat.drive.linkNotFound') : t('chat.drive.searchFailed'));
      } finally {
        if (seq === searchSeq.current) setSearching(false);
      }
    }, 280);
    return () => window.clearTimeout(handle);
  }, [query, open, connection?.connection_id, canList]); // eslint-disable-line react-hooks/exhaustive-deps

  // Browse tab — folder contents.
  const currentFolderId = crumbs[crumbs.length - 1]?.id ?? null;
  useEffect(() => {
    if (!open || !canList || tab !== 'browse') return;
    let cancelled = false;
    setBrowsing(true);
    Promise.all([
      driveService.listFolders(currentFolderId || undefined),
      driveService.listFiles(currentFolderId || undefined),
    ])
      .then(([f, files]) => { if (!cancelled) { setFolders(f); setFolderFiles(files); setCursor(0); } })
      .catch((err) => {
        if (cancelled) return;
        setFolders([]); setFolderFiles([]);
        if (err?.response?.status === 409) void loadConnections();
        else if (!gate.refusedBy(err)) setError(t('chat.drive.loadFailed'));
      })
      .finally(() => { if (!cancelled) setBrowsing(false); });
    return () => { cancelled = true; };
  }, [open, tab, currentFolderId, connection?.connection_id, canList]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleFiles: DriveFileRef[] = useMemo(() => {
    if (query.trim()) return results ?? [];
    if (tab === 'browse') return folderFiles;
    return recent ?? [];
  }, [query, results, tab, folderFiles, recent]);
  const visibleFolders: DriveFolder[] = !query.trim() && tab === 'browse' ? folders : [];
  const rowCount = visibleFolders.length + visibleFiles.length;
  const loadingList = query.trim() ? searching : tab === 'browse' ? browsing : recent === null;

  const isAttached = useCallback((id: string) => alreadyAttached.includes(id), [alreadyAttached]);
  const atCapacity = selected.size + alreadyAttached.length >= maxSelectable;

  const toggle = useCallback((file: DriveFileRef) => {
    if (isAttached(file.file_id)) return;
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(file.file_id)) next.delete(file.file_id);
      else if (next.size + alreadyAttached.length < maxSelectable) next.set(file.file_id, file);
      return next;
    });
  }, [isAttached, alreadyAttached.length, maxSelectable]);

  const openFolder = (folder: DriveFolder) => {
    setCrumbs((prev) => [...prev, { id: folder.folder_id, name: folder.name }]);
  };

  const confirm = useCallback(() => {
    if (selected.size === 0) return;
    onAdd(Array.from(selected.values()));
    onClose();
  }, [selected, onAdd, onClose]);

  // Keyboard navigation over the flat row list (folders first, then files).
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); confirm(); return; }
    if (rowCount === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(rowCount - 1, c + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === 'Enter' || (e.key === ' ' && e.target === listRef.current)) {
      e.preventDefault();
      if (cursor < visibleFolders.length) openFolder(visibleFolders[cursor]);
      else toggle(visibleFiles[cursor - visibleFolders.length]);
    }
  };

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-row="${cursor}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  // Escape closes from anywhere — the dialog's own key handler only sees keys
  // typed inside it, and the not-connected state has nothing to focus.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const notConnected = !connectionsLoading && !connection;

  return createPortal(
    <div className="drive-picker__overlay" onMouseDown={onClose} role="presentation">
      <div
        className="drive-picker"
        role="dialog"
        aria-modal="true"
        aria-label={t('chat.drive.addFromDrive')}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <header className="drive-picker__header">
          <span className="drive-picker__logo"><GoogleDriveGlyph size={20} /></span>
          <div className="drive-picker__heading">
            <h2 className="drive-picker__title">{t('chat.drive.addFromDrive')}</h2>
            {connection && <span className="drive-picker__account">{connection.account_email}</span>}
          </div>
          <button type="button" className="drive-picker__close" onClick={onClose} aria-label={t('common.close')}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </header>

        {notConnected ? (
          <div className="drive-picker__connect">
            <GoogleDriveGlyph size={40} />
            <h3>{t('chat.drive.notConnectedTitle')}</h3>
            <p>{t('chat.drive.notConnectedBody')}</p>
            <button type="button" className="drive-picker__btn drive-picker__btn--primary" onClick={() => void connect('google_drive')}>
              {t('chat.drive.connect')}
            </button>
          </div>
        ) : gate.needsRead ? (
          <GoogleAccessPrompt
            access="read"
            reason="pick"
            onGranted={() => { gate.reset(); setError(null); }}
            onDeclined={onClose}
          />
        ) : (
          <>
            <div className="drive-picker__search">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
              <input
                ref={inputRef}
                type="text"
                dir="auto"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('chat.drive.searchPlaceholder')}
                aria-label={t('chat.drive.searchPlaceholder')}
                spellCheck={false}
              />
              {query && (
                <button type="button" className="drive-picker__clear" onClick={() => setQuery('')} aria-label={t('chat.drive.clearSearch')}>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              )}
            </div>

            {!query.trim() && (
              <div className="drive-picker__tabs" role="tablist">
                <button type="button" role="tab" aria-selected={tab === 'recent'} className={tab === 'recent' ? 'is-active' : ''} onClick={() => setTab('recent')}>
                  {t('chat.drive.recent')}
                </button>
                <button type="button" role="tab" aria-selected={tab === 'browse'} className={tab === 'browse' ? 'is-active' : ''} onClick={() => setTab('browse')}>
                  {t('chat.drive.browse')}
                </button>
                {tab === 'browse' && (
                  <nav className="drive-picker__crumbs" aria-label={t('chat.drive.browse')}>
                    {crumbs.map((c, i) => (
                      <React.Fragment key={`${c.id ?? 'root'}-${i}`}>
                        {i > 0 && <span className="drive-picker__crumb-sep" aria-hidden="true">/</span>}
                        <button
                          type="button"
                          className={`drive-picker__crumb ${i === crumbs.length - 1 ? 'is-current' : ''}`}
                          onClick={() => setCrumbs((prev) => prev.slice(0, i + 1))}
                        >
                          {i === 0 ? t('chat.drive.myDrive') : c.name}
                        </button>
                      </React.Fragment>
                    ))}
                  </nav>
                )}
              </div>
            )}

            <div className="drive-picker__list" ref={listRef} role="listbox" aria-multiselectable="true" tabIndex={0}>
              {loadingList ? (
                <div className="drive-picker__state"><Spin /></div>
              ) : error && rowCount === 0 ? (
                <div className="drive-picker__state drive-picker__state--error">{error}</div>
              ) : rowCount === 0 ? (
                <div className="drive-picker__state">
                  {query.trim() ? t('chat.drive.noResults') : tab === 'browse' ? t('chat.drive.emptyFolder') : t('chat.drive.noRecent')}
                </div>
              ) : (
                <>
                  {visibleFolders.map((folder, i) => (
                    <button
                      type="button"
                      key={folder.folder_id}
                      data-row={i}
                      className={`drive-picker__row drive-picker__row--folder ${cursor === i ? 'is-cursor' : ''}`}
                      onClick={() => openFolder(folder)}
                      onMouseEnter={() => setCursor(i)}
                    >
                      <span className="drive-picker__row-icon"><DriveKindIcon kind="folder" /></span>
                      <span className="drive-picker__row-name" dir="auto">{folder.name}</span>
                      <svg className="drive-picker__row-chevron" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
                    </button>
                  ))}
                  {visibleFiles.map((file, j) => {
                    const i = visibleFolders.length + j;
                    const kind = driveKind(file.mime_type, file.name);
                    const attached = isAttached(file.file_id);
                    const checked = attached || selected.has(file.file_id);
                    const disabled = attached || (!checked && atCapacity);
                    return (
                      <div
                        key={file.file_id}
                        data-row={i}
                        role="option"
                        aria-selected={checked}
                        aria-disabled={disabled}
                        className={`drive-picker__row ${checked ? 'is-selected' : ''} ${cursor === i ? 'is-cursor' : ''} ${disabled ? 'is-disabled' : ''}`}
                        onClick={() => !disabled && toggle(file)}
                        onMouseEnter={() => setCursor(i)}
                      >
                        <span className="drive-picker__row-icon"><DriveKindIcon kind={kind} /></span>
                        <span className="drive-picker__row-text">
                          <span className="drive-picker__row-name" dir="auto" title={file.name}>{file.name}</span>
                          <span className="drive-picker__row-meta">
                            {t(`chat.drive.kinds.${kind}`)}
                            {file.modified_time && ` · ${formatRelativeTime(file.modified_time)}`}
                            {file.size != null && ` · ${formatBytes(file.size)}`}
                            {attached && ` · ${t('chat.drive.alreadyAdded')}`}
                          </span>
                        </span>
                        <span className={`drive-picker__check ${checked ? 'is-on' : ''}`} aria-hidden="true">
                          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
                        </span>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            <footer className="drive-picker__footer">
              <span className="drive-picker__count">
                {selected.size > 0
                  ? t('chat.drive.selectedCount', { count: selected.size })
                  : t('chat.drive.hint')}
              </span>
              <div className="drive-picker__footer-actions">
                <button type="button" className="drive-picker__btn" onClick={onClose}>{t('common.cancel')}</button>
                <button type="button" className="drive-picker__btn drive-picker__btn--primary" disabled={selected.size === 0} onClick={confirm}>
                  {selected.size > 1 ? t('chat.drive.addCount', { count: selected.size }) : t('chat.drive.add')}
                </button>
              </div>
            </footer>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
